import { useEffect, useRef } from 'react';
import { useLoaderData, useLocation, useNavigate } from 'react-router';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import type { loader as videosLoader } from '@modules/professional/page/loaders/videos-page-loader.server';
import type { VideoTileProp } from '@modules/professional/type/company-videos';
import { DetailTileMedia } from '~/ui-rocco/pages/detail-shared';
import { NoSearchResults } from '@modules/professional/component/shared/NoSearchResults';
import { toEmbedUrl } from '../utils/video-embed';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle,
} from '@rocco/components/dialog';
import { useTracker } from '@archipro-website/tracker';
import { cn } from '@rocco/utils/cn';
import {
    TAB_BAR_GRID_GAP,
    TAB_CONTENT_GAP,
    TAB_RULE_GAP,
} from '../utils/tab-spacing';
import { DirectoryDisplayDropdown } from '~/ui-rocco/pages/directory-browse/DirectoryDisplayDropdown';
import {
    DIRECTORY_DESKTOP_COL_CLASSES,
    useDirectoryDisplayColumns,
} from '~/ui-rocco/pages/directory-browse/directory-display-columns';
import { ProfessionalTabListingBar } from './ProfessionalTabListingBar';
import { useInPageSearchTerm } from '../hooks/use-in-page-search-term';

const coverSrcOf = (coverImage: VideoTileProp['coverImage']): string =>
    typeof coverImage === 'string' ? coverImage : (coverImage.src ?? '');

/**
 * A single editorial video tile: a 16:9 cover with a play affordance that swaps
 * to an autoplaying embed iframe on click (mounted client-side only).
 */
const VideoTileItem = ({
    video,
    onPlay,
}: {
    video: VideoTileProp;
    onPlay: (video: VideoTileProp, trigger: HTMLButtonElement) => void;
}) => {
    const cover = coverSrcOf(video.coverImage);

    return (
        <div className="flex flex-col gap-[0.625rem]">
            <div className="bg-neutral-200 relative aspect-video w-full overflow-hidden rounded-tile">
                <button
                    type="button"
                    onClick={(event) => onPlay(video, event.currentTarget)}
                    aria-label={`Play ${video.caption}`}
                    className="group absolute inset-0 block size-full"
                >
                    <DetailTileMedia
                        src={cover}
                        alt={video.caption}
                        className="size-full"
                    />
                    <span className="absolute inset-0 flex items-center justify-center">
                        <span className="flex items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-sm transition-colors group-hover:bg-black/70 max-md:size-12 md:size-[4.5rem]">
                            <svg
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                aria-hidden="true"
                                className="max-md:size-5 max-md:translate-x-[1px] md:size-7 md:translate-x-[2px]"
                            >
                                <path d="M8 5v14l11-7z" />
                            </svg>
                        </span>
                    </span>
                </button>
            </div>
            <p className="m-0 font-sans leading-[1.3] font-normal text-[#181818] max-md:text-base md:text-[0.875rem]">
                {video.caption}
            </p>
        </div>
    );
};

/**
 * Videos tab (/videos) body — a refine (sort) + search bar over a 3-column grid
 * of 16:9 click-to-play video tiles, matching the other item tabs. Sort
 * navigates the `sortBy` URL the loader reads; search filters by caption
 * client-side. Self-hides when empty; the name / stats / CTA sit in the
 * persistent profile header above.
 */
export const ProfessionalVideosGridSection = () => {
    const { companyVideosResult } = useLoaderData<typeof videosLoader>();
    const { desktop } = useAppDisplayModeContext();
    const location = useLocation();
    const navigate = useNavigate();
    const tracker = useTracker();
    const videos = (companyVideosResult?.videos ?? []) as VideoTileProp[];

    // Matched in the browser, because the tab loads its whole set — but the
    // term still lives in the URL, so a narrowed list can be shared and
    // survives a reload like every other tab's.
    const [query, setQuery] = useInPageSearchTerm();
    const term = query.trim().toLowerCase();
    const shownVideos = term
        ? videos.filter((video) =>
              (video.caption ?? '').toLowerCase().includes(term),
          )
        : videos;

    // The open video lives in the URL (?video=<id>) exactly as the legacy tab
    // did, so a specific video stays linkable and Back closes the player
    // instead of leaving the page.
    const openVideoId = new URLSearchParams(location.search).get('video');
    const activeVideo =
        videos.find((v) => String(v.id) === openVideoId) ?? null;
    const triggerRef = useRef<HTMLButtonElement | null>(null);

    const setOpenVideo = (
        video: VideoTileProp | null,
        trigger?: HTMLButtonElement | null,
    ) => {
        if (trigger) triggerRef.current = trigger;
        const params = new URLSearchParams(location.search);
        if (video) params.set('video', String(video.id));
        else params.delete('video');
        const search = params.toString();
        navigate(`${location.pathname}${search ? `?${search}` : ''}`, {
            preventScrollReset: true,
            // CustomScrollRestoration also honours this so opening the player
            // cannot fall through to a top restore when only ?video= changes.
            state: { scroll: false },
        });
    };

    // Logged from an effect rather than the click handler: the click fires
    // before the ?video= param lands, so the logged URL carried no video
    // identity, and a deep-linked open never went through the handler at all.
    //
    // Keyed on the *resolved* video rather than the raw param: a ?video= value
    // matching nothing in this professional's set opens no dialog, and must not
    // log a play. One event per video actually opened, however it opened.
    const activeVideoId = activeVideo?.id;
    useEffect(() => {
        if (!activeVideoId) return;
        tracker.log('Video', { url: new URL(window.location.href) });
    }, [activeVideoId, tracker]);

    // Three across by default: a video tile carries a title under it and is
    // watched rather than scanned, so it wants more width than the Network
    // tab's five.
    const { columns, setColumns } = useDirectoryDisplayColumns(
        'professional-videos',
        3,
    );

    if (videos.length === 0) return null;

    return (
        <section className="w-full overflow-hidden bg-ground text-[#1f1f21]">
            {/* Desktop keeps the editorial title + intro + CTA cluster; on mobile
                the persistent social-profile header carries the identity/CTAs and
                the tab goes straight to results. */}
            {desktop && (
                <>
                    <div
                        className={cn(
                            'mx-auto w-full max-w-(--ap-page-width) md:px-[3.132rem]',
                            TAB_CONTENT_GAP,
                        )}
                    >
                        {/* The heading repeated the tab pill above it; kept as sr-only
                            so the tab still has a heading in the document outline. */}
                        <h2 className="sr-only">Videos</h2>
                    </div>
                    <div className="mx-auto w-full max-w-(--ap-page-width) md:px-[3.132rem]">
                        {/* Desktop only: on mobile this is the first thing
                            under the tab strip, whose own border already
                            closes off the nav, so a second rule a hairline
                            below it reads as one thick line. */}
                        <div className="h-px w-full bg-black/20 max-md:hidden" />
                    </div>
                </>
            )}

            {/* The listing tabs' bar: filter, search, and Display at the
                row's end. Outside the desktop gate: a phone needs to filter a
                long list more than a desktop does. */}
            <div
                className={cn(
                    'mx-auto w-full max-w-(--ap-page-width) max-md:px-6 max-md:pt-3 md:px-[3.132rem]',
                    TAB_RULE_GAP,
                )}
            >
                <ProfessionalTabListingBar
                    search={{
                        value: query,
                        onChange: setQuery,
                        placeholder: 'Search Videos',
                    }}
                    display={
                        desktop ? (
                            // Remembered per tab rather than followed out to
                            // the browse grids.
                            <DirectoryDisplayDropdown
                                columns={columns}
                                onColumnsChange={setColumns}
                            />
                        ) : null
                    }
                />
            </div>

            {shownVideos.length === 0 ? (
                <div
                    className={cn(
                        'mx-auto w-full max-w-(--ap-page-width) max-md:px-6 max-md:pt-9 max-md:pb-14 md:px-[3.132rem] md:pb-[7.5rem]',
                        TAB_BAR_GRID_GAP,
                    )}
                >
                    <NoSearchResults resultsType="videos" />
                </div>
            ) : (
                <div
                    className={cn(
                        'mx-auto grid w-full max-w-(--ap-page-width) grid-cols-1 max-md:gap-y-8 max-md:px-6 max-md:pt-9 md:gap-x-3 md:gap-y-[3.6rem] md:px-[3.132rem]',
                        TAB_BAR_GRID_GAP,
                        DIRECTORY_DESKTOP_COL_CLASSES[columns],
                    )}
                >
                    {shownVideos.map((video) => (
                        <VideoTileItem
                            key={video.id}
                            video={video}
                            onPlay={(video, trigger) =>
                                setOpenVideo(video, trigger)
                            }
                        />
                    ))}
                </div>
            )}

            {/* Player overlay. DialogContent is not force-mounted, so closing
                unmounts the iframe and the audio stops with it - do not add
                forceMount here. */}
            <Dialog
                open={activeVideo !== null}
                onOpenChange={(open) => {
                    if (!open) setOpenVideo(null);
                }}
            >
                {activeVideo ? (
                    <DialogContent
                        spacing="none"
                        // Keep the page where the reader was. Radix otherwise
                        // focuses the dialog on open, which scrolls the
                        // document to the top. On close, return focus to the
                        // play button without moving the viewport.
                        onOpenAutoFocus={(event) => event.preventDefault()}
                        onCloseAutoFocus={(event) => {
                            event.preventDefault();
                            triggerRef.current?.focus({ preventScroll: true });
                        }}
                        // The default close control is near-black, which all but
                        // disappears on this surface; "rounded" is the light-on-
                        // dark variant. It matters more than usual here: once a
                        // reader clicks into the player, focus belongs to the
                        // cross-origin iframe and Escape no longer reaches the
                        // dialog, so this button is the only way out.
                        closeButtonVariant="rounded"
                        // Width is 92vw to match prod, but also capped against
                        // the viewport height: the player is 16:9, so sizing on
                        // width alone overflowed short-but-wide windows and
                        // carried the close control off-screen with it. With
                        // scroll locked and Escape captured by the iframe, that
                        // left no way out. 7rem covers the top strip plus a
                        // margin below.
                        //
                        // The surface is transparent with that strip of top
                        // padding rather than a black box tight to the video, so
                        // the close control sits above the player instead of
                        // overlapping its chrome.
                        className="w-[min(92vw,calc((100vh-7rem)*16/9))] max-w-none border-0 bg-transparent p-0 pt-14 shadow-none sm:rounded-none"
                        overlayClassName="bg-black/80"
                    >
                        <DialogTitle className="sr-only">
                            {activeVideo.caption}
                        </DialogTitle>
                        <DialogDescription className="sr-only">
                            Video player
                        </DialogDescription>
                        {toEmbedUrl(activeVideo.src) ? (
                            <iframe
                                src={toEmbedUrl(activeVideo.src) ?? undefined}
                                title={activeVideo.caption}
                                className="aspect-video w-full rounded-tile bg-black"
                                allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
                                allowFullScreen
                                tabIndex={-1}
                            />
                        ) : null}
                    </DialogContent>
                ) : null}
            </Dialog>
        </section>
    );
};
